import { useEffect, useState } from "react";
import { X, Tag, Trash2 } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllShippingCompanies,
  getPricesByCompany,
} from "../../redux/actions";
import axiosInstance from "../../redux/helpers/axios";
import toast from "react-hot-toast";

export default function ManageRatesModal({ open, onClose, category }) {
  const dispatch = useDispatch();
  const { companies, prices } = useSelector((s) => s.shipping);

  const [rates, setRates] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [selectedPriceId, setSelectedPriceId] = useState("");
  const [loading, setLoading] = useState(false);

  // load category's current rates + companies on open
  useEffect(() => {
    if (!open || !category) return;
    setRates(category.shippingRates || []);
    setSelectedCompanyId("");
    setSelectedPriceId("");
    dispatch(getAllShippingCompanies());
  }, [open, category, dispatch]);

  // load prices when company changes
  const handleCompanyChange = (companyId) => {
    setSelectedCompanyId(companyId);
    setSelectedPriceId("");
    if (companyId) dispatch(getPricesByCompany(companyId));
  };

  const handleAttach = async () => {
    if (!selectedCompanyId || !selectedPriceId) {
      return toast.error("Select a company and price first.");
    }

    const exists = rates.find(
      (r) =>
        r.shippingCompanyId === Number(selectedCompanyId) &&
        r.priceId === Number(selectedPriceId),
    );
    if (exists) return toast.error("This rate is already attached.");

    try {
      setLoading(true);
      const res = await axiosInstance.post("/admin/shipping/rate/attach", {
        categoryId: category.id,
        shippingCompanyId: Number(selectedCompanyId),
        priceId: Number(selectedPriceId),
      });

      if (res.status === 201) {
        setRates((prev) => [...prev, res.data.rate]);
        setSelectedPriceId("");
        toast.success("Rate attached successfully.");
      }
    } catch (error) {
      const data = error?.response?.data;
      toast.error(data?.msg || "Error attaching rate.");
    } finally {
      setLoading(false);
    }
  };

  const handleDetach = async (rateId) => {
    try {
      setLoading(true);
      const res = await axiosInstance.delete(`/admin/shipping/rate/${rateId}`);
      if (res.status === 200) {
        setRates((prev) => prev.filter((r) => r.id !== rateId));
        toast.success("Rate removed.");
      }
    } catch (error) {
      toast.error("Error removing rate.");
    } finally {
      setLoading(false);
    }
  };

  const getPriceLabel = (rate) => {
    const p = rate.price;
    if (!p) return `Price #${rate.priceId}`;
    return `${p.contains} — ${p.rate} ${p.currency}/${p.unit}${p.shipmentType ? ` (${p.shipmentType})` : ""}`;
  };

  const getCompanyName = (rate) => {
    return rate.shippingCompany?.name || `Company #${rate.shippingCompanyId}`;
  };

  const filteredPrices = prices.filter(
    (p) => p.shippingCompanyId === Number(selectedCompanyId),
  );

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9999]">
      <div
        className="absolute inset-0 bg-black/30 backdrop-blur-[2px]"
        onClick={onClose}
      />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl ring-1 ring-slate-200">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-slate-100 p-4">
            <div className="flex items-center gap-2">
              <Tag className="h-5 w-5 text-slate-700" />
              <div>
                <span className="font-semibold text-slate-900">
                  Manage Rates
                </span>
                <span className="ml-2 text-sm text-slate-400">
                  {category?.name}
                </span>
              </div>
            </div>
            <button
              onClick={onClose}
              className="rounded-xl p-2 hover:bg-slate-100"
            >
              <X className="h-5 w-5 text-slate-600" />
            </button>
          </div>

          <div className="p-4 space-y-4">
            {/* Attach new rate */}
            <div className="rounded-2xl border border-slate-200 p-4 space-y-3">
              <p className="text-sm font-semibold text-slate-800">
                Attach a Rate
              </p>
              <div className="grid grid-cols-2 gap-2">
                <select
                  value={selectedCompanyId}
                  onChange={(e) => handleCompanyChange(e.target.value)}
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-200 bg-white"
                >
                  <option value="">Select company</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                <select
                  value={selectedPriceId}
                  onChange={(e) => setSelectedPriceId(e.target.value)}
                  disabled={!selectedCompanyId || filteredPrices.length === 0}
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-200 bg-white disabled:opacity-50"
                >
                  <option value="">Select price</option>
                  {filteredPrices.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.contains} — {p.rate} {p.currency}/{p.unit}
                      {p.shipmentType ? ` (${p.shipmentType})` : ""}
                    </option>
                  ))}
                </select>
              </div>
              <button
                onClick={handleAttach}
                disabled={loading || !selectedCompanyId || !selectedPriceId}
                className="rounded-xl bg-emerald-500 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Attaching..." : "+ Attach Rate"}
              </button>
            </div>

            {/* Current attached rates */}
            <div className="space-y-2">
              <p className="text-sm font-semibold text-slate-800">
                Attached Rates
                <span className="ml-2 text-xs font-normal text-slate-400">
                  {rates.length} total
                </span>
              </p>

              {rates.length === 0 ? (
                <div className="rounded-xl bg-slate-50 px-4 py-6 text-center text-sm text-slate-400">
                  No rates attached yet.
                </div>
              ) : (
                <div className="max-h-60 overflow-y-auto space-y-2">
                  {rates.map((rate) => (
                    <div
                      key={rate.id}
                      className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50 px-3 py-2.5"
                    >
                      <div className="text-sm">
                        <span className="font-medium text-slate-800">
                          {getCompanyName(rate)}
                        </span>
                        <span className="mx-1.5 text-slate-300">·</span>
                        <span className="text-slate-600">
                          {getPriceLabel(rate)}
                        </span>
                      </div>
                      <button
                        onClick={() => handleDetach(rate.id)}
                        disabled={loading}
                        className="ml-3 rounded-lg bg-rose-50 p-1.5 text-rose-500 hover:bg-rose-100 disabled:opacity-50"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Close */}
            <div className="flex justify-end pt-1">
              <button
                onClick={onClose}
                className="rounded-xl bg-slate-100 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
